import { useState, useEffect } from 'react'
import { useToast } from '@chakra-ui/react'
import { Research, ResearchTag } from '../../../../types/Research'
import {
  fetchResearch,
  fetchResearchTags,
  filterResearchByLetter,
} from '../../../../utils/researchUtils'

export const useResearch = () => {
  const [research, setResearch] = useState<Research[]>([])
  const [tags, setTags] = useState<ResearchTag[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedLetter, setSelectedLetter] = useState<string>('')
  const [filteredResearch, setFilteredResearch] = useState<Research[]>([])

  const toast = useToast()

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const [researchData, tagsData] = await Promise.all([
          fetchResearch(),
          fetchResearchTags(),
        ])
        setResearch(researchData)
        setTags(tagsData)
      } catch (error) {
        toast({
          title: 'Error loading research data',
          description: 'Please try again later',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [toast])

  // Filter research based on search, tags, and letter
  useEffect(() => {
    let filtered = research

    // Apply search and tag filters
    if (searchTerm || selectedTags.length > 0) {
      filtered = research.filter((item) => {
        const searchLower = searchTerm.toLowerCase()
        const matchesSearch =
          !searchTerm ||
          item.title.toLowerCase().includes(searchLower) ||
          item.abstract.toLowerCase().includes(searchLower) ||
          item.tags.some((tag) => {
            const tagData = tags.find((t) => t.id === tag)
            return tagData?.name.toLowerCase().includes(searchLower)
          })

        const matchesTags =
          selectedTags.length === 0 ||
          selectedTags.some((tag) => item.tags.includes(tag))

        return matchesSearch && matchesTags
      })
    }

    // Apply letter filter
    if (selectedLetter) {
      filtered = filterResearchByLetter(filtered, selectedLetter)
    }

    setFilteredResearch(filtered)
  }, [research, searchTerm, selectedTags, selectedLetter, tags])

  // Handle letter click from AZ selector
  const handleLetterClick = (letter: string) => {
    setSelectedLetter(selectedLetter === letter ? '' : letter)
  }

  return {
    // Data
    research,
    tags,
    filteredResearch,
    loading,

    // Filter state
    searchTerm,
    selectedTags,
    selectedLetter,

    // Actions
    setSearchTerm,
    setSelectedTags,
    handleLetterClick,
  }
}
